import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { ForbiddenIcon } from "@images/svgIcons/forbiddenIcon"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { roleBoxStaffPermissions } from "@modules/boxStaff/utils/boxStaffUtils"
import { word } from "@modules/core/utils/i18n"
import { SharedCoachRole } from "@modules/sharedCoach/sharedCoachDtos"
import { HUText } from "@modules/ui/components/huText"
import { Skeleton } from "primereact/skeleton"
import { useTheme } from "styled-components"

type role = BoxStaffRole | SharedCoachRole

// check: true means that the user has the permission
export const permissionsRole = [
  {
    role: BoxStaffRole.COACH,
    features: [
      {
        text: "box.staff.permissionsModal.feature.planning.seeTimetables",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_TIMETABLES),
      },
      {
        text: "box.staff.permissionsModal.feature.planning.seeSlots",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
      },
      {
        text: "box.staff.permissionsModal.feature.programming.manageProgrammings",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
      },
      {
        text: "box.staff.permissionsModal.feature.offers.seeList",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_OFFERS),
      },
      {
        text: "box.staff.permissionsModal.feature.members.seeListofLeads",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_LEADS),
      },
      {
        text: "box.staff.permissionsModal.feature.members.seeStaffMember",
        check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_STAFF),
      },
    ],
  },
  {
    role: BoxStaffRole.CO_OWNER,
    features: [
      {
        text: "huPermissions.features.coOwner.feature1",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.MANAGE_SLOTS),
      },
      {
        text: "huPermissions.features.coOwner.feature2",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
      },
      {
        text: "huPermissions.features.coOwner.feature3",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.EDIT_OFFER),
      },
      {
        text: "huPermissions.features.coOwner.feature4",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.DELETE_GYM),
      },
      {
        text: "huPermissions.features.coOwner.feature5",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.EDIT_MEMBER),
      },
      {
        text: "huPermissions.features.coOwner.feature6",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.DELETE_GYM),
      },
      {
        text: "huPermissions.features.coOwner.feature7",
        check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.SEE_FINANCIAL),
      },
    ],
  },
  {
    role: BoxStaffRole.OWNER,
    features: [
      {
        text: "huPermissions.features.coOwner.feature1",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.MANAGE_SLOTS),
      },
      {
        text: "huPermissions.features.coOwner.feature2",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
      },
      {
        text: "huPermissions.features.coOwner.feature3",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.EDIT_OFFER),
      },
      {
        text: "huPermissions.features.coOwner.feature5",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.EDIT_MEMBER),
      },
      {
        text: "huPermissions.features.coOwner.feature7",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.SEE_FINANCIAL),
      },
      {
        text: "huPermissions.features.owner.feature1",
        check: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(BoxStaffPermissions.EDIT_GYM_INFORMATION),
      },
    ],
  },
  {
    role: BoxStaffRole.HEAD_COACH,
    features: [
      { text: "huPermissions.features.headCoach.feature1", check: true },
      {
        text: "box.staff.permissionsModal.feature.planning.addSlot",
        check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
      },
      {
        text: "box.staff.permissionsModal.feature.planning.createActivity",
        check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.CREATE_ACTIVITY),
      },
      {
        text: "box.staff.permissionsModal.feature.planning.editSlots",
        check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
      },
      {
        text: "box.staff.permissionsModal.feature.planning.editSession",
        check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
      },
      {
        text: "box.staff.permissionsModal.feature.members.editMemberFile",
        check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.EDIT_MEMBER),
      },
    ],
  },
  {
    role: BoxStaffRole.FINANCIAL,
    features: [
      {
        text: "huPermissions.features.coOwner.feature7",
        check: roleBoxStaffPermissions[BoxStaffRole.FINANCIAL].includes(BoxStaffPermissions.SEE_FINANCIAL),
      },
    ],
  },
  {
    role: SharedCoachRole.STANDARD,
    features: [
      { text: "invitationDetailsInfo.createSessions", check: true },
      { text: "invitationDetailsInfo.editSessions", check: true },
      { text: "invitationDetailsInfo.waitingRoomManagement", check: true },
      { text: "invitationDetailsInfo.accessToProgramSetting", check: false },
    ],
  },
]

export type HuPermissionsProps = {
  title?: string
  button?: React.JSX.Element
  role?: role // if role is missing the component will show a skeleton
} & React.HTMLAttributes<HTMLDivElement>

export const HuPermissions: React.FC<HuPermissionsProps> = ({ role, title, button, ...props }) => {
  const theme = useTheme()
  const defaultClassName = "flex flex-column"

  return (
    <div className={props.className ? props.className : defaultClassName} {...props}>
      <div className="flex align-items-center">
        {title && (
          <HUText type="label" color={theme.neutralColor700} fontStyle="LS" className="flex align-items-end">
            {title.toUpperCase()}
          </HUText>
        )}
        {button}
      </div>
      {!role ? (
        <div className="flex w-full">
          <Skeleton height="12rem" borderRadius="8px" />
        </div>
      ) : (
        <div className="flex justify-content-between w-full mx-2 p-4 bg-neutralColor-50 border-round-xl mt-2">
          <div className="grid grid-nogutter w-full">
            {permissionsRole
              .find((p) => p.role === role)
              ?.features.map((feature, index) => (
                <div
                  key={index}
                  className={`col-6 flex align-items-center gap-2 px-3 py-2 ${
                    index % 2 ? "" : "border-right-1"
                  } border-neutralColor-200`}
                >
                  {feature.check ? <CheckmarkIcon color={theme.sucess} /> : <ForbiddenIcon color={theme.error} />}

                  <HUText type="label" color={theme.neutralColor700} fontStyle="LM">
                    {word(feature.text as any)}
                  </HUText>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
